import React, { Fragment } from 'react';
import { Box, Button, Divider, Fade, Menu, Typography } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import { ConnectedUserDto } from '../../../services/dtos/connected-user-dto';
import MenuHeaderItem from '../menu-header-item/menu-header-item';
import {
    AccountCircle,
    AdminPanelSettings,
    Article,
    DirectionsWalk,
    Equalizer,
    HistoryEdu,
    HomeWork,
    Logout,
    MailOutline,
    Person,
    PersonSearch,
    Receipt,
    ReceiptLong,
    Widgets,
} from '@mui/icons-material';
import { useAuth } from '../../../hooks/use-auth.hook';
import { ROLE_ADMIN, ROLE_COMM } from '../../../utils/constants.utils';
import { logout } from '../../../services/auth.service';
import { showAxiosMessageError } from '../../../services/error-message.service';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { env } from '../../../env';

/**
 * Composant gérant l'affichage du menu de navigation quand l'utilisateur est connecté.
 *
 * - Côté ordinateur : un menu pour la liste des services, un autre pour la partie utilisateur (profil et déconnexion)
 * - Côté mobile : le même menu utilisateur, et un menu burger
 * @param props
 * @constructor
 */
export default function MenuHeader(props: { name: string; user: null | ConnectedUserDto }) {
    //Mise en place des useState
    const [serviceEl, setServiceEl] = React.useState<null | HTMLElement>(null);
    const [userEl, setUserEl] = React.useState<null | HTMLElement>(null);
    const [burgerEl, setBurgerEl] = React.useState<null | HTMLElement>(null);
    const [adminEl, setAdminEl] = React.useState<null | HTMLElement>(null);

    //Ajout des constantes
    const openUser = Boolean(userEl);
    //Menu burger pour formaat mobile
    const openBurger = Boolean(burgerEl);
    //Menu du service pour écran large
    const openService = Boolean(serviceEl);
    //Menu propre à l'administrateur
    const openAdmin = Boolean(adminEl);
    //Vérification authentification de l'utilisateur
    const auth = useAuth();
    // Snackbars
    const showMessage = useSnackbars();

    // Gestion de la fermeture du menu profil
    const handleProfileClose = () => {
        setUserEl(null);
    };

    // Gestion de la fermeture du menu hamburger
    const handleBurgerClose = () => {
        setBurgerEl(null);
    };

    // Gestion de la fermeture du menu services
    const handleServiceClose = () => {
        setServiceEl(null);
    };

    // Gestion de la fermeture du menu admin
    const handleAdminClose = () => {
        setAdminEl(null);
    };

    // Gestion du click sur le menu hamburger
    const handleClickBurger = (event: React.MouseEvent<HTMLElement>) => {
        setBurgerEl(event.currentTarget);
    };

    // Gestion du click sur le menu services
    const handleClickService = (event: React.MouseEvent<HTMLElement>) => {
        setServiceEl(event.currentTarget);
    };

    // Gestion du click sur un élément du menu profil
    const handleClickProfile = (event: React.MouseEvent<HTMLElement>) => {
        setUserEl(event.currentTarget);
    };

    // Gestion du click sur le menu admin
    const handleClickAdmin = (event: React.MouseEvent<HTMLElement>) => {
        setAdminEl(event.currentTarget);
    };

    // Gestion du click sur le bouton de déconnexion
    const handleClickLogout = () => {
        logout()
            .catch(error => {
                console.error('Erreur lors de la déconnexion');
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                auth.signout();
                setUserEl(null);
            });
    };

    // Le menu va contenir des éléments différents selon si on clique sur le bouton profile, service...
    let menu = null;
    switch (props.name) {
        // Menu profil
        // Mon-profile
        // Déconnexion
        case 'profile':
            menu = (
                <Box sx={{ width: '100%' }}>
                    {/* Icône du menu */}
                    <Button
                        aria-haspopup="true"
                        aria-expanded={openUser ? 'true' : undefined}
                        disableElevation
                        onClick={handleClickProfile}
                    >
                        <AccountCircle />
                        <Typography variant="h6" sx={{ display: { xs: 'none', md: 'flex' } }}>
                            {props.user?.nom} {props.user?.prenom}
                        </Typography>
                    </Button>
                    {/*Définition du menu*/}
                    <Menu
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        anchorEl={userEl}
                        open={openUser}
                        onClose={handleProfileClose}
                        TransitionComponent={Fade}
                    >
                        {/* Sous-menu */}
                        {/*Mon profile*/}
                        <MenuHeaderItem
                            handle={handleProfileClose}
                            text="Mon profil"
                            href={'/profil'}
                            icon={<Person sx={{ marginRight: '10px' }} />}
                        />
                        {/*Deconnexion*/}
                        <MenuHeaderItem
                            handle={handleClickLogout}
                            text="Déconnexion"
                            href={null}
                            icon={<Logout sx={{ marginRight: '10px' }} />}
                        />
                    </Menu>
                </Box>
            );
            break;
        // Menu service
        case 'service':
            // Menu Service principal, n'apparaît que si connecté
            menu = (
                <Box sx={{ width: '100%' }}>
                    {/*Mes services*/}
                    <Button
                        aria-haspopup="true"
                        aria-expanded={openService ? 'true' : undefined}
                        disableElevation
                        onClick={handleClickService}
                    >
                        <Widgets />
                        <Typography variant="h6" sx={{ display: { xs: 'none', md: 'flex' } }}>
                            Mes services
                        </Typography>
                    </Button>
                    {/*definition du menu*/}
                    <Menu
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        anchorEl={serviceEl}
                        open={openService}
                        onClose={handleServiceClose}
                        TransitionComponent={Fade}
                    >
                        {/* Sous-menu */}
                        {/*J'emmenage*/}
                        <MenuHeaderItem
                            handle={handleServiceClose}
                            text="J'emménage"
                            href={'/j-emmenage'}
                            icon={<DirectionsWalk sx={{ marginRight: '10px' }} />}
                        />
                        {/*Mes contrats*/}
                        <MenuHeaderItem
                            handle={handleServiceClose}
                            text="Mes contrats"
                            href={'/mes-contrats'}
                            icon={<HistoryEdu sx={{ marginRight: '10px' }} />}
                        />
                        {/*Mes factures*/}
                        <MenuHeaderItem
                            handle={handleServiceClose}
                            text="Mes factures"
                            href={'/mes-factures'}
                            icon={<ReceiptLong sx={{ marginRight: '10px' }} />}
                        />
                        {/*Mes consommations*/}
                        <MenuHeaderItem
                            handle={handleServiceClose}
                            text="Mes consommations"
                            href={'/mes-consommations'}
                            icon={<Equalizer sx={{ marginRight: '10px' }} />}
                        />
                        {/*Mes demandes*/}
                        <MenuHeaderItem
                            handle={handleServiceClose}
                            text="Mes demandes"
                            href={'/mes-demandes'}
                            icon={<Receipt sx={{ marginRight: '10px' }} />}
                        />
                    </Menu>
                </Box>
            );
            break;
        // Menu hamburger affiché sur le mobile
        case 'burger':
            let burger = (
                <Box sx={{ width: '100%' }}>
                    {/* partie "générale" */}
                    <MenuHeaderItem
                        handle={handleBurgerClose}
                        text="Accueil"
                        href="/"
                        icon={<HomeIcon sx={{ marginRight: '10px' }} />}
                    />
                    {/*Si une personne est connectée*/}
                    {props.user ? (
                        <>
                            <Divider />
                            {/* Sous-menu */}
                            {/*J'emmenage*/}
                            <MenuHeaderItem
                                handle={handleBurgerClose}
                                text="J'emménage"
                                href={'/j-emmenage'}
                                icon={<DirectionsWalk sx={{ marginRight: '10px' }} />}
                            />
                            {/*mes contrats*/}
                            <MenuHeaderItem
                                handle={handleBurgerClose}
                                text="Mes contrats"
                                href={'/mes-contrats'}
                                icon={<HistoryEdu sx={{ marginRight: '10px' }} />}
                            />
                            {/*mes factures*/}
                            <MenuHeaderItem
                                handle={handleBurgerClose}
                                text="Mes factures"
                                href={'/mes-factures'}
                                icon={<ReceiptLong sx={{ marginRight: '10px' }} />}
                            />
                            {/*Mes demandes*/}
                            <MenuHeaderItem
                                handle={handleBurgerClose}
                                text="Mes demandes"
                                href={'/mes-demandes'}
                                icon={<Receipt sx={{ marginRight: '10px' }} />}
                            />
                            {/*mes services*/}
                            <MenuHeaderItem
                                handle={handleBurgerClose}
                                text="Mes consommations"
                                href={'/mes-consommations'}
                                icon={<Equalizer sx={{ marginRight: '10px' }} />}
                            />
                        </>
                    ) : (
                        <>
                            <Divider />
                            <MenuHeaderItem
                                handle={handleBurgerClose}
                                text="Se connecter"
                                href={'/connexion'}
                                icon={<Person sx={{ marginRight: '10px' }} />}
                            />
                            <Divider />
                            {/* Partie démarches en ligne */}
                            <MenuHeaderItem
                                handle={handleBurgerClose}
                                text="Démarches en ligne"
                                href={'/demarches-en-ligne'}
                                icon={<HistoryEdu sx={{ marginRight: '10px' }} />}
                            />
                            {/* Partie contactez-nous */}
                            <MenuHeaderItem
                                handle={handleBurgerClose}
                                text="Contact"
                                href={'/contactez-nous'}
                                icon={<MailOutline sx={{ marginRight: '10px' }} />}
                            />
                        </>
                    )}
                    {/*Si admin */}
                    {props.user?.roles?.some(role => role === ROLE_ADMIN) ? (
                        <>
                            <Divider />
                            {/* Partie gestion des pages */}
                            <MenuHeaderItem
                                handle={handleAdminClose}
                                text="Administration des pages"
                                href={'/administration/pages'}
                                icon={<Article sx={{ marginRight: '10px' }} />}
                            />
                            {/* Partie impersonnalisation */}
                            <MenuHeaderItem
                                handle={handleAdminClose}
                                text="Recherche compte abonné"
                                href={'/administration/recherche-abonne'}
                                icon={<PersonSearch sx={{ marginRight: '10px' }} />}
                            />
                        </>
                    ) : (
                        <></>
                    )}
                    {/*Si comm */}
                    {props.user?.roles?.some(role => role === ROLE_COMM) ? (
                        <>
                            <Divider />
                            {/* Partie gestion des pages */}
                            <MenuHeaderItem
                                handle={handleAdminClose}
                                text="Administration des pages"
                                href={'/administration/pages'}
                                icon={<Article sx={{ marginRight: '10px' }} />}
                            />
                        </>
                    ) : (
                        <></>
                    )}
                </Box>
            );

            // Création du menu
            menu = (
                <Box sx={{ flexFlow: 'row', width: '100%' }}>
                    {/*bouton burger*/}
                    <Button
                        aria-haspopup="true"
                        aria-expanded={openBurger ? 'true' : undefined}
                        size="medium"
                        onClick={handleClickBurger}
                    >
                        <MenuIcon />
                    </Button>
                    {/*definition du menu*/}
                    <Menu
                        anchorEl={burgerEl}
                        open={openBurger}
                        onClose={handleBurgerClose}
                        TransitionComponent={Fade}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        {burger}
                    </Menu>
                </Box>
            );
            break;
        // Menu admin
        case 'admin':
            // Menu admin principal, n'apparaît que si connecté et que la personne est un admin
            menu = (
                <Box sx={{ width: '100%' }}>
                    {/*Partie admin*/}
                    <Button
                        aria-haspopup="true"
                        aria-expanded={openAdmin ? 'true' : undefined}
                        disableElevation
                        onClick={handleClickAdmin}
                    >
                        <AdminPanelSettings />
                        <Typography variant="h6" sx={{ display: { xs: 'none', md: 'flex' } }}>
                            Administration
                        </Typography>
                    </Button>
                    <Menu
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                        anchorEl={adminEl}
                        open={openAdmin}
                        onClose={handleAdminClose}
                        TransitionComponent={Fade}
                    >
                        {/* Sous-menu */}
                        {/*Gestion des pages*/}
                        <MenuHeaderItem
                            handle={handleAdminClose}
                            text="Gestion des pages"
                            href={'/administration/pages'}
                            icon={<Article sx={{ marginRight: '10px' }} />}
                        />
                        {env.REACT_APP_THEME === 'sddea' && props.user?.roles?.some(role => role === ROLE_ADMIN) && (
                            <MenuHeaderItem
                                handle={handleAdminClose}
                                text="Recherche compte abonné"
                                href={'/administration/recherche-abonne'}
                                icon={<PersonSearch sx={{ marginRight: '10px' }} />}
                            />
                        )}
                        {/* Informations par établissement */}
                        {env.REACT_APP_THEME === 'sddea' && (
                            <MenuHeaderItem
                                handle={handleAdminClose}
                                text="Informations par établissement"
                                href={'/administration/information-etablissement'}
                                icon={<HomeWork sx={{ marginRight: '10px' }} />}
                            />
                        )}
                    </Menu>
                </Box>
            );
            break;
    }

    return <Fragment>{menu}</Fragment>;
}
